import { productStore } from './productStore';
import { asAmount, newLineId } from './budget';
import type { CostKind } from './quarterCosts';

/** Nobody registers them, so a row carries its own name and rate and the matrix cannot price it. */
export interface FractionalPerson {
  /** Its own id, so two rows that end up with the same name are still two rows. */
  id: string;
  name: string;
  /** €/h as agreed with them. Null until somebody fills it in, which prices their hours at nothing. */
  rate: number | null;
}

/** A row as the grid holds it while it is being edited: the rate is still whatever was typed. */
export interface FractionalDraft {
  id: string;
  name: string;
  rate: string;
}

/** Per year, like the team's own rates: somebody bought in again next year is agreed again next year. */
export const rosterStorageKey = (productId: string, year: string): string => `productFractional:${productId}:${year}`;

export const fractionalHoursKey = (kind: CostKind, productId: string, sprintId: string): string =>
  `productFractionalHours:${kind}:${productId}:${sprintId}`;

/** The same id maker the bought-in cost lines use: these are mock-store rows in the same sense. */
export const newFractionalId = newLineId;

/** Null for a blank field; undefined for something that is not a rate, which holds Save. */
export const asRate = (value: string): number | null | undefined => {
  if (value.trim() === '') return null;
  const amount = asAmount(value);
  return amount === null || amount === 0 ? undefined : amount;
};

const asPerson = (raw: unknown): FractionalPerson | null => {
  if (!raw || typeof raw !== 'object') return null;
  const { id, name, rate } = raw as Record<string, unknown>;
  if (typeof id !== 'string' || !id) return null;
  const named = typeof name === 'string' ? name.trim() : '';
  if (!named) return null;
  const priced = asAmount(rate);
  return { id, name: named, rate: priced === null || priced === 0 ? null : priced };
};

// A value that does not parse reads as nobody bought in.
export const readRoster = (productId: string, year: string): FractionalPerson[] => {
  try {
    const raw = productStore.getItem(rosterStorageKey(productId, year));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed)) return [];
    return parsed.map(asPerson).filter((person): person is FractionalPerson => person !== null);
  } catch {
    return [];
  }
};

export const writeRoster = (productId: string, year: string, people: FractionalPerson[]): void => {
  productStore.setItem(rosterStorageKey(productId, year), JSON.stringify(people));
};

export const readFractionalHours = (kind: CostKind, productId: string, sprintId: string): Record<string, number> => {
  try {
    const raw = productStore.getItem(fractionalHoursKey(kind, productId, sprintId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return {};
    const hours: Record<string, number> = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([id, value]) => {
      const amount = asAmount(value);
      // A row with no hours in a sprint is not a row budgeted at nothing.
      if (amount !== null && amount !== 0) hours[id] = amount;
    });
    return hours;
  } catch {
    return {};
  }
};

export const writeFractionalHours = (
  kind: CostKind,
  productId: string,
  sprintId: string,
  hours: Record<string, number>,
): void => {
  productStore.setItem(fractionalHoursKey(kind, productId, sprintId), JSON.stringify(hours));
};

/** Only rows with a name are people; the rest of the draft is a row somebody started and left. */
export const resolveRoster = (rows: FractionalDraft[]): FractionalPerson[] =>
  rows.flatMap(row => {
    const rate = asRate(row.rate);
    return row.name.trim() === '' || rate === undefined ? [] : [{ id: row.id, name: row.name.trim(), rate }];
  });

/** What an hour of a fractional row costs: their own rate, or nothing agreed yet. */
export const fractionalRate = (roster: FractionalPerson[], personId: string): number | null =>
  roster.find(person => person.id === personId)?.rate ?? null;

/** Whether a row belongs to the roster rather than to the team, which decides where its hours go. */
export const isFractional = (roster: FractionalPerson[], personId: string): boolean =>
  roster.some(person => person.id === personId);
